import { Component, EventEmitter, Input, Output, OnInit, ViewChild, ElementRef } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { CoreService } from "../../../services/core.service";
import { forkJoin, of } from "rxjs";
import { map, catchError, take } from "rxjs/operators";
import { SidebarComponent } from "../../sidebars/sidebar/sidebar.component";
import { environment } from "../../../../environments/environment";
import {
  SocialPlatformI,
  ContentSiteI,
  RssFeedI,
} from "../../../interfaces/core.interface";
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from "@angular/cdk/drag-drop";
import { Router } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: "app-social-ai-create-post-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatCheckbox,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    DragDropModule,
    MatTooltipModule,
    SidebarComponent,
  ],
  templateUrl: "./social-ai-create-post-form.component.html",
  styleUrl: "./social-ai-create-post-form.component.scss",
})
export class SocialAiCreatePostFormComponent implements OnInit {
  isDev = !environment.isProduction;
  
  @Input() formData: any = {} as any;
  @Output() onSubmitClbk = new EventEmitter<any>();
  @ViewChild('modalContainer') modalContainer!: ElementRef;

  postForm!: FormGroup;
  isEditMode = false;
  isEditBlocked = false;
  isDataLoaded = false;
  isAiModalOpen = false;

  companyID = 0;
  sites: ContentSiteI[] = [];
  platforms: SocialPlatformI[] = [];
  feeds: RssFeedI[] = [];
  filteredPlatforms: SocialPlatformI[] = [];
  filteredTags: string[] = [];
  filteredPostStatuses: { value: string; label: string }[] = [];

  selectedFile: File | null = null;
  imagePreview: string | null = null;
  uploadedFiles: { file: File | null; preview: string; alt: string }[] = [];
  private previousTag: string | null = null;

  urlsArry: string[] = [];
  videoFileName: string | null = null;
  videoTypes: any[] = [];

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = "./assets/images/na.png";

  private readonly LATEST_POSTS_KEY = "latestPosts";
  private readonly LATEST_ALERTS_KEY = "latestAlerts";

  textDirection: "ltr" | "rtl" = "ltr";
  setTextDirection(dir: "ltr" | "rtl") {
    this.textDirection = dir;
  }

  coverFrames: { time: number; dataUrl: string }[] = [];
  selectedCoverFrameDataUrl: string | null = null;
  selectedCoverFrameFile: File | null = null;
  private readonly COVER_FRAME_COUNT = 8;

  displayTagMap: Record<string, string> = {
    "Facebook Link Post": "photolink",
    "Facebook Photo Post": "fbphotopost",
    "Facebook Photo Post (1st comment)": "fbfirstcomment",
    "Facebook Video Post": "fbvideo",
    "Facebook Reel Post": "fbreel",
    "Facebook Story Photo": "fbstoryphoto",
    "Facebook Story Video": "fbstoryvideo",
    "Facebook Story Reel": "fbstoryreel",
    "Instagram Photo Post": "igphotopost",
    "Instagram Video Post": "igvideo",
    "Instagram Reel Post": "igreel",
    "Instagram Carousel Post": "igcarousel",
    "Instagram Story Video": "igstoryvideo",
    "Instagram Story Photo": "igstoryphoto",
    "Instagram Story Reel": "igstoryreel",
    "TikTok Video Post": "tiktokvideo",
    "TikTok Reel Post": "tiktokreel",
    "TikTok Carousel Post": "tiktokcarousel",
    "TikTok Story Photo": "tiktokstoryphoto",
    "TikTok Story Video": "tiktokstoryvideo",
    "TikTok Story Reel": "tiktokstoryreel",
  };

  videoStoryTags = [
    "fbvideo",
    "igvideo",
    "fbstoryvideo",
    "igstoryvideo",
    "tiktokstoryvideo",
    "tiktokvideo",
    "fbreel",
    "fbstoryreel",
    "igreel",
    "igstoryreel",
    "tiktokreel",
    "tiktokstoryreel",
  ];

  mediaTags = [
    "igcarousel",
    "fbstoryvideo",
    "igstoryvideo",
    "tiktokstoryvideo",
    "tiktokcarousel",
    "fbreel",
    "fbstoryreel",
    "igreel",
    "igstoryreel",
    "tiktokreel",
    "tiktokstoryreel",
  ];

  singleImageTags = [
    "fbphotopost",
    "fbfirstcomment",
    "fbstoryphoto",
    "igphotopost",
    "igstoryphoto",
    "tiktokstoryphoto",
  ];

  fbTags = [
    "fbphotopost",
    "fbfirstcomment",
    "photolink",
    "fbvideo",
    "fbreel",
    "fbstoryphoto",
    "fbstoryvideo",
    "fbstoryreel",
  ];

  igTags = [
    "igphotopost",
    "igcarousel",
    "igvideo",
    "igreel",
    "igstoryphoto",
    "igstoryvideo",
    "igstoryreel",
  ];

  tiktokTags = [
    "tiktokcarousel",
    "tiktokstoryphoto",
    "tiktokvideo",
    "tiktokreel",
    "tiktokstoryvideo",
    "tiktokstoryreel",
  ];

  allVideoTags = [
    "fbvideo",
    "igvideo",
    "tiktokvideo",
    "fbstoryvideo",
    "igstoryvideo",
    "tiktokstoryvideo",
  ];

  reelTags = ["igreel", "fbreel", "tiktokreel"];

  multiImageTags = [
    "igreel",
    "fbreel",
    "tiktokreel",
    "igstoryreel",
    "fbstoryreel",
    "tiktokstoryreel",
    "igcarousel",
    "tiktokcarousel",
  ];

  postStatuses = [
    { value: "Scheduled", label: "Scheduled" },
    { value: "ScheduledToPostNow", label: "Scheduled To Post Now" },
    { value: "Draft", label: "Draft" },
    { value: "ScheduledToFbStory", label: "Scheduled To Facebook Story" },
    { value: "ScheduledToInstaStory", label: "Scheduled To Instagram Story" },
    { value: "ScheduledToTikTokStory", label: "Scheduled To TikTok Story" },
  ];

  disableScheduleStatuses = [
    "ScheduledToPostNow",
    "ScheduledToFbStory",
    "ScheduledToInstaStory",
    "ScheduledToTikTokStory",
  ];

  private readonly MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  constructor(
    private readonly fb: FormBuilder,
    private readonly coreService: CoreService,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.initFormSubscriptions();

    this.companyID = this.coreService.user?.companyID || 0;

    if (!this.companyID) {
      this.coreService.showSnackbar("No company found.");
      return;
    }

    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites = sites as ContentSiteI[];
      this.platforms = platforms as SocialPlatformI[];
      this.initValueChanges();
      this.isDataLoaded = true;
    });

    this.loadSidebarPosts();
    this.loadAlertsFromStorage();

    this.loadVideoTypes().subscribe((videoTypes) => {
      this.videoTypes = videoTypes;
    });
  }

  private initFormSubscriptions(): void {
    this.postForm
      .get("post_status")
      ?.valueChanges.subscribe((status: string) =>
        this.updateScheduledForState(status),
      );

    this.postForm.get("video_type_id")?.valueChanges.subscribe((id) => {
      const type = this.videoTypes.find(v => v.id === id)?.video_type_name || "";
      this.postForm.patchValue({ video_type: type }, { emitEvent: false });

      this.postForm.patchValue(
        { video_file: null, video_url: "" },
        { emitEvent: false },
      );

      this.clearControlValidators("video_file", "video_url");

      if (type === "video_upload") {
        this.postForm.get("video_file")?.setValidators([Validators.required]);
      } else if (type === "video_url") {
        this.postForm.get("video_url")?.setValidators([
          Validators.required,
          Validators.pattern(/youtube\.com\/watch\?v=|youtu\.be\//),
        ]);
      }

      this.updateControlsValidity("video_file", "video_url");
    });
  }

  async onVideoFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;

    const file = input.files[0];
    if (file.type !== "video/mp4") {
      input.value = "";
      return;
    }

    this.videoFileName = file.name;
    this.postForm.patchValue({ video_file: file });
    this.postForm.get("video_file")?.markAsTouched();

    this.coverFrames = [];
    this.selectedCoverFrameDataUrl = null;
    this.selectedCoverFrameFile = null;
    this.postForm.patchValue({ cover_frame: null });

    await this.generateCoverFramesFromVideo(file);
  }

  getDisplayTagsForPlatform(platformTags: string[]): string[] {
    return platformTags
      .map((tag) =>
        Object.keys(this.displayTagMap).find(
          (key) => this.displayTagMap[key] === tag,
        ),
      )
      .filter(Boolean) as string[];
  }

  private updateScheduledForState(status: string): void {
    const ctrl = this.postForm.get("scheduled_for");
    if (this.disableScheduleStatuses.includes(status)) {
      ctrl?.setValue(null);
      ctrl?.disable();
    } else {
      ctrl?.enable();
    }
    ctrl?.updateValueAndValidity();
  }

  private filterPostStatusesByTag(tag: string): void {
    if (["igstoryphoto", "igstoryvideo", "igstoryreel"].includes(tag)) {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) => s.value === "ScheduledToInstaStory",
      );
      this.setPostStatus("ScheduledToInstaStory");
    } else if (["fbstoryphoto", "fbstoryvideo", "fbstoryreel"].includes(tag)) {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) => s.value === "ScheduledToFbStory",
      );
      this.setPostStatus("ScheduledToFbStory");
    } else if (
      ["tiktokstoryphoto", "tiktokstoryvideo", "tiktokstoryreel"].includes(tag)
    ) {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) => s.value === "ScheduledToTikTokStory",
      );
      this.setPostStatus("ScheduledToTikTokStory");
    } else {
      this.filteredPostStatuses = this.postStatuses.filter(
        (s) =>
          ![
            "ScheduledToFbStory",
            "ScheduledToInstaStory",
            "ScheduledToTikTokStory",
          ].includes(s.value),
      );
      this.postForm.get("post_status")?.reset("", { emitEvent: false });
      this.updateScheduledForState("");
    }
  }

  private setPostStatus(val: string) {
    this.postForm.get("post_status")?.setValue(val, { emitEvent: false });
    this.updateScheduledForState(val);
  }

  initValueChanges(): void {
    this.postForm.get("site")?.valueChanges.subscribe((siteDomain: string) => {
      const selectedSite = this.sites.find((s) => s.Domain === siteDomain);
      if (!selectedSite) return;

      this.postForm.get("content_site_id")?.setValue(selectedSite.ID);
      this.feeds = [];
      this.filteredPlatforms = [];

      this.postForm.patchValue({
        platform: "",
        rss_feed_id: null,
        tag: "",
        rss_article_url: "",
        rss_description: "",
      });

      this.coreService
        .getData(`/contentsiterssfeed/${selectedSite.ID}`)
        .pipe(
          take(1),
          catchError(() => of({ data: [] })),
        )
        .subscribe((res: any) => {
          this.feeds = res.data || [];
          this.postForm.get("rss_feed_id")?.enable();
        });

      this.filteredPlatforms = this.platforms.filter(
        (p) => p.site_id === selectedSite.ID,
      );
      this.clearImages();
    });

    this.postForm.get("rss_feed_id")?.valueChanges.subscribe((id: number) => {
      const selectedFeed = this.feeds.find((f) => f.id === id);
      if (selectedFeed?.url) {
        this.postForm.patchValue({ rss_article_url: selectedFeed.url });
      }
    });

    this.postForm.get("platform")?.valueChanges.subscribe((platform: string) => {
      const p = (platform || "").toLowerCase();
      if (p.includes("facebook")) this.filteredTags = this.fbTags;
      else if (p.includes("instagram")) this.filteredTags = this.igTags;
      else if (p.includes("tiktok")) this.filteredTags = this.tiktokTags;
      else this.filteredTags = [];

      this.postForm.patchValue({ tag: this.filteredTags[0] || "" });
      this.previousTag = null;
    });

    this.postForm.get("tag")?.valueChanges.subscribe((newTag: string) => {
      this.resetVideoControls();

      this.videoFileName = null;

      this.filterPostStatusesByTag(newTag);
      this.setRssUrlValidators(newTag);

      const newIsSingle = this.singleImageTags.includes(newTag);
      const newIsReel = this.reelTags.includes(newTag);

      const prevIsSingle = this.previousTag
        ? this.singleImageTags.includes(this.previousTag)
        : false;
      const prevIsReel = this.previousTag
        ? this.reelTags.includes(this.previousTag)
        : false;

      if (!(newIsSingle && prevIsSingle) && !(newIsReel && prevIsReel)) {
        this.clearImages();
      }

      const platform = (this.postForm.get("platform")?.value || "")
        .toLowerCase();

      if (this.allVideoTags.includes(newTag)) {
        this.postForm.get("video_type")?.setValidators([Validators.required]);

        if (platform.includes("instagram")) {
          this.postForm.patchValue(
            { video_type: "video_upload" },
            { emitEvent: true },
          );
        } else {
          this.postForm.patchValue(
            { video_type: "", video_type_id: null },
            { emitEvent: false },
          );
        }
      } else {
        this.postForm.get("video_type")?.clearValidators();
        this.postForm.patchValue(
          { video_type: "", video_type_id: null },
          { emitEvent: false },
        );
      }

      this.postForm.get("video_type")?.updateValueAndValidity({
        emitEvent: false,
      });

      this.previousTag = newTag;
    });
  }

  private resetVideoControls() {
    const videoTypeCtrl = this.postForm.get("video_type");
    const videoFileCtrl = this.postForm.get("video_file");
    const videoUrlCtrl = this.postForm.get("video_url");

    videoTypeCtrl?.reset();
    videoFileCtrl?.reset();
    videoUrlCtrl?.reset();

    this.clearControlValidators("video_type", "video_file", "video_url");

    [videoTypeCtrl, videoFileCtrl, videoUrlCtrl].forEach((ctrl) => {
      ctrl?.markAsPristine();
      ctrl?.markAsUntouched();
    });

    this.updateControlsValidity("video_type", "video_file", "video_url");
  }

  private clearControlValidators(...names: string[]) {
    names.forEach((n) => this.postForm.get(n)?.clearValidators());
  }

  private updateControlsValidity(...names: string[]) {
    names.forEach((n) =>
      this.postForm.get(n)?.updateValueAndValidity({ emitEvent: false }),
    );
  }

  loadSites() {
    return this.coreService
      .getData(`/sites-with-feeds/${this.companyID}`)
      .pipe(
        take(1),
        map((res: any) => (res.data || []).filter((site: any) => site.hasFeed)),
        catchError(() => of([])),
      );
  }

  loadPlatforms() {
    return this.coreService.getData("/getSocialPlatforms").pipe(
      take(1),
      map((res: any) =>
        (res.data as SocialPlatformI[]).filter(
          (p) => p.isApproved === "Approved" && !!p.access_token,
        ),
      ),
      catchError(() => of([])),
    );
  }

  loadVideoTypes() {
    return this.coreService.getData(`/getvideotypes`).pipe(
      take(1),
      map((res: any) => res.dataVideoTypes || []),
      catchError(() => of([])),
    );
  }

  clearImages(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    this.uploadedFiles.forEach((uf) => URL.revokeObjectURL(uf.preview));
    this.uploadedFiles = [];
  }

  dropImages(event: CdkDragDrop<any[]>): void {
    moveItemInArray(
      this.uploadedFiles,
      event.previousIndex,
      event.currentIndex,
    );
    this.updateFormArrays();
  }

  clearAll(): void {
    this.uploadedFiles = [];
    this.updateFormArrays();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];

    if (file.size > this.MAX_IMAGE_SIZE) {
      this.coreService.showSnackbar(
        "Image too large. Maximum allowed size is 5MB.",
      );
      input.value = "";
      return;
    }

    if (file.type === "image/webp") {
      this.coreService.showSnackbar(
        "WebP images are not supported. Please upload JPG or PNG.",
      );
      input.value = "";
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = () => (this.imagePreview = reader.result as string);
    reader.readAsDataURL(file);
  }

  removeImage(): void {
    this.selectedFile = null;
    this.imagePreview = null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files) this.addFiles(event.dataTransfer.files);
  }

  onMultipleFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    Array.from(input.files).forEach((file) => {
      if (file.size > this.MAX_IMAGE_SIZE) {
        this.coreService.showSnackbar(`Image "${file.name}" exceeds 5MB limit.`);
        return;
      }

      if (file.type === "image/webp") {
        this.coreService.showSnackbar(
          "WebP images are not supported. Please upload JPG or PNG.",
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedFiles.push({ file, preview: e.target.result, alt: "" });
        this.updateFormArrays();
      };
      reader.readAsDataURL(file);
    });

    input.value = "";
  }

  
  async openAiImageModal(): Promise<void> {
    this.isAiModalOpen = true;
    window.dispatchEvent(new CustomEvent("aiModalOpen"));

    setTimeout(async () => {
      if (!this.modalContainer?.nativeElement) {
        this.coreService.showSnackbar("AI modal container not found.");
        return;
      }

      const originalFetch = window.fetch;
      const originalXhrOpen = XMLHttpRequest.prototype.open;
      const originalXhrSend = XMLHttpRequest.prototype.send;

      try {
        const { cloudToken, uid } = await this.getAiEngineContext();

        const cloudUrl = environment.aiEngineUrl;
        const container = this.modalContainer.nativeElement as HTMLElement;

        container.innerHTML = "";

        /*
        * Log fetch requests/responses triggered by the AI Engine.
        */
        window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
          const [input, init] = args;

          console.group("[AI Engine][fetch] Request");
          console.log("URL:", input);
          console.log("Method:", init?.method ?? "GET");
          console.log("Headers:", init?.headers);
          console.log("Body:", init?.body);
          console.log("Full request arguments:", args);
          console.groupEnd();

          try {
            const response = await originalFetch(...args);
            const responseClone = response.clone();

            let responseBody: unknown;

            try {
              responseBody = await responseClone.json();
            } catch {
              try {
                responseBody = await responseClone.text();
              } catch {
                responseBody = "Unable to read response body";
              }
            }

            console.group("[AI Engine][fetch] Response");
            console.log("URL:", response.url);
            console.log("Status:", response.status);
            console.log("Status text:", response.statusText);
            console.log(
              "Headers:",
              Object.fromEntries(response.headers.entries())
            );
            console.log("Body:", responseBody);
            console.log("Full response:", response);
            console.groupEnd();

            return response;
          } catch (error) {
            console.error("[AI Engine][fetch] Request failed:", {
              input,
              init,
              error,
            });

            throw error;
          }
        };

        /*
        * Log XMLHttpRequest requests/responses in case the bridge
        * does not use fetch.
        */
        XMLHttpRequest.prototype.open = function (
          method: string,
          url: string | URL,
          async: boolean = true,
          username?: string | null,
          password?: string | null
        ): void {
          (this as XMLHttpRequest & {
            __aiRequestData?: {
              method: string;
              url: string;
            };
          }).__aiRequestData = {
            method,
            url: url.toString(),
          };

          originalXhrOpen.call(
            this,
            method,
            url,
            async,
            username ?? null,
            password ?? null
          );
        };

        XMLHttpRequest.prototype.send = function (
          body?: Document | XMLHttpRequestBodyInit | null
        ): void {
          const xhr = this as XMLHttpRequest & {
            __aiRequestData?: {
              method: string;
              url: string;
            };
          };

          console.group("[AI Engine][XHR] Request");
          console.log("URL:", xhr.__aiRequestData?.url);
          console.log("Method:", xhr.__aiRequestData?.method);
          console.log("Body:", body);
          console.log("XHR instance:", xhr);
          console.groupEnd();

          this.addEventListener("load", () => {
            let responseBody: unknown = this.response;

            if (
              typeof this.responseText === "string" &&
              this.responseText.length > 0
            ) {
              try {
                responseBody = JSON.parse(this.responseText);
              } catch {
                responseBody = this.responseText;
              }
            }

            console.group("[AI Engine][XHR] Response");
            console.log("URL:", xhr.__aiRequestData?.url);
            console.log("Method:", xhr.__aiRequestData?.method);
            console.log("Status:", this.status);
            console.log("Status text:", this.statusText);
            console.log("Headers:", this.getAllResponseHeaders());
            console.log("Body:", responseBody);
            console.log("XHR instance:", this);
            console.groupEnd();
          });

          this.addEventListener("error", () => {
            console.error("[AI Engine][XHR] Request failed:", {
              url: xhr.__aiRequestData?.url,
              method: xhr.__aiRequestData?.method,
              body,
              xhr: this,
            });
          });

          originalXhrSend.call(this, body);
        };

        console.log("[AI Engine] Importing bridge module...");

        const bridgeModule: any = await import(
          /* webpackIgnore: true */ `${cloudUrl}/lib/AiEngineBridge.js?v=${Date.now()}`
        );

        console.log("[AI Engine] Bridge module loaded:", bridgeModule);

        if (typeof bridgeModule.init !== "function") {
          throw new Error("AiEngineBridge.init is not a function");
        }

        if (typeof bridgeModule.mount_window !== "function") {
          throw new Error(
            "AiEngineBridge.mount_window is missing from named exports."
          );
        }

        console.group("[AI Engine] Initialization");
        console.log("UID:", uid);
        console.log("Cloud token exists:", Boolean(cloudToken));
        console.log("Asset types:", ["text", "image"]);
        console.log("Container:", container);
        console.groupEnd();

        bridgeModule.init("publio", cloudToken);

        console.log(
          "[AI Engine] Waiting for asset selection / Use selected..."
        );

        const assets = await bridgeModule.mount_window(
          { uid },
          ["image"],
          container
        );

        /*
        * Αυτό θα εκτελεστεί μόλις το mount_window επιστρέψει
        * τα selected assets, συνήθως μετά το "Use selected".
        */
        console.group("[AI Engine][Use selected] Assets received");
        console.log("Raw assets response:", assets);
        console.log("Assets type:", typeof assets);
        console.log("Is array:", Array.isArray(assets));

        if (Array.isArray(assets)) {
          console.log("Assets count:", assets.length);

          assets.forEach((asset: unknown, index: number) => {
            console.log(`Asset ${index + 1}:`, asset);
          });
        }

        try {
          console.log(
            "Serialized assets:",
            JSON.stringify(assets, null, 2)
          );
        } catch (serializationError) {
          console.warn(
            "[AI Engine] Assets could not be serialized:",
            serializationError
          );
        }

        console.groupEnd();

        this.handleAiGeneratedAssets(assets);

        console.log(
          "[AI Engine][Use selected] handleAiGeneratedAssets completed"
        );
      } catch (error) {
        console.error("[AI Engine] Error mounting AI window:", error);
        this.coreService.showSnackbar("Failed to load AI image generator.");
      } finally {
        /*
        * Επαναφορά των original request handlers αφού επιστρέψουν τα assets
        * ή αποτύχει το modal.
        */
        window.fetch = originalFetch;
        XMLHttpRequest.prototype.open = originalXhrOpen;
        XMLHttpRequest.prototype.send = originalXhrSend;

        console.log("[AI Engine] Request interceptors restored");
      }
    }, 0);
  }

  private handleAiGeneratedAssets(assets: any): void {
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      return;
    }

    // Convert AI-generated assets to uploaded files
    const newUploadedFiles = assets.map((asset: any) => {
      // Create a blob from the image URL
      return {
        file: null, // We'll keep this as null since it's a remote URL
        preview: asset.url || asset.preview || asset,
        alt: asset.alt || ""
      };
    });

    // Add the new assets to the existing uploaded files
    this.uploadedFiles = [...this.uploadedFiles, ...newUploadedFiles];
    
    // Update form arrays to reflect the new files
    this.updateFormArrays();
    
    // Show success message
    this.coreService.showSnackbar(`${assets.length} AI-generated image(s) added successfully.`);
  }

  private async getAiEngineContext(): Promise<{ cloudToken: string; uid: string }> {
    const storedCompanyUid =
      localStorage.getItem("company_uid") ||
      localStorage.getItem("cloud_company_uid");

    const tokenRes: any = await this.coreService
      .getCloudToken(storedCompanyUid)
      .toPromise();

    const cloudToken = tokenRes?.cloud_token;
    const uid = tokenRes?.uid || tokenRes?.company_uid;
    const companyId = tokenRes?.company_id;

    if (cloudToken) {
      localStorage.setItem("cloud_token", cloudToken);
    }

    if (uid) {
      localStorage.setItem("company_uid", String(uid));
      localStorage.setItem("cloud_company_uid", String(uid));
    }

    if (companyId) {
      localStorage.setItem("company_id", String(companyId));
    }

    if (!cloudToken) {
      throw new Error("Cloud token is missing from response");
    }

    if (!uid) {
      throw new Error("Company uid is missing from cloud token response");
    }

    return {
      cloudToken,
      uid: String(uid),
    };
  }

  closeAiModal(): void {
    this.isAiModalOpen = false;
    window.dispatchEvent(new CustomEvent("aiModalClose"));
  }

  addFiles(files: FileList): void {
    Array.from(files).forEach((file) => {
      if (file.size > this.MAX_IMAGE_SIZE) {
        this.coreService.showSnackbar(`Image "${file.name}" exceeds 5MB limit.`);
        return;
      }

      if (file.type === "image/webp") {
        this.coreService.showSnackbar(
          "WebP images are not supported. Please upload JPG or PNG.",
        );
        return;
      }

      const preview = URL.createObjectURL(file);
      this.uploadedFiles.push({ file, preview, alt: "" });
    });

    this.updateFormArrays();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.updateFormArrays();
  }

  updateFormArrays(): void {
    this.postForm.patchValue({
      image_array: this.uploadedFiles.map((f) => f.file),
      alt_text_array: this.uploadedFiles.map((f) => f.alt),
    });
  }

  private setRssUrlValidators(tag: string): void {
    const rssUrlControl = this.postForm.get("rss_article_url");
    const urlPattern = Validators.pattern(
      /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/\S*)?$/,
    );

    if (
      [
        "photolink",
        "fbstoryphoto",
        "fbstoryvideo",
        "igstoryvideo",
        "igstoryphoto",
        "tiktokstoryphoto",
        "tiktokstoryvideo",
      ].includes(tag)
    ) {
      rssUrlControl?.setValidators([Validators.required, urlPattern]);
    } else {
      rssUrlControl?.setValidators([urlPattern]);
    }

    rssUrlControl?.updateValueAndValidity({ emitEvent: false });
  }

  submit(): void {
    const selectedSpecificTag = this.postForm.get("tag")?.value;
    this.setRssUrlValidators(selectedSpecificTag);

    if (this.postForm.invalid) {
      this.postForm.markAllAsTouched();
      this.coreService.showSnackbar("Please complete all required fields.");
      return;
    }

    const formRaw = this.postForm.getRawValue();
    const scheduledFor = formRaw.scheduled_for;

    if (scheduledFor) {
      const selectedDate = new Date(scheduledFor);
      const now = new Date();
      selectedDate.setSeconds(0, 0);
      now.setSeconds(0, 0);
      if (selectedDate.getTime() < now.getTime()) {
        this.coreService.showSnackbar(
          "The scheduled date and time cannot be in the past.",
        );
        return;
      }
    }

    const selectedTag = formRaw.tag;

    if (this.singleImageTags.includes(selectedTag)) {
      const hasSingleImage =
        !!this.selectedFile ||
        !!this.imagePreview ||
        this.uploadedFiles.length >= 1; // Allow at least one image for single image tags

      if (!hasSingleImage) {
        this.coreService.showSnackbar(
          "Please select at least one image for this tag.",
        );
        return;
      }
      
      // For single image tags, if multiple images are selected, use only the first one
      if (this.uploadedFiles.length > 1) {
        this.uploadedFiles = [this.uploadedFiles[0]];
        this.updateFormArrays();
        this.coreService.showSnackbar("Multiple images detected for single image tag. Using the first image.");
      }
    }

    if (this.reelTags.includes(selectedTag)) {
      if (this.uploadedFiles.length < 4) {
        this.coreService.showSnackbar("Reels require at least 4 images.");
        return;
      }
      if (this.uploadedFiles.length > 10) {
        this.coreService.showSnackbar("You cannot upload more than 10 images.");
        return;
      }
    }

    const selectedPlatform = this.filteredPlatforms.find(
      (fp: SocialPlatformI) => fp.platform === formRaw.platform,
    );
    const socialPlatformId = selectedPlatform?.id?.toString() || "";

    const formData = new FormData();
    formData.append("company_id", this.companyID.toString());
    formData.append(
      "content_site_id",
      formRaw.content_site_id?.toString() || "",
    );
    formData.append("rss_feed_id", formRaw.rss_feed_id?.toString() || "");
    formData.append("social_platform_id", socialPlatformId);
    formData.append("social_tag", formRaw.tag || "");
    formData.append("post_status", formRaw.post_status || "");
    formData.append("article_url", formRaw.rss_article_url || "");
    formData.append("rss_description", formRaw.rss_description || "");
    formData.append("post_title", formRaw.post_title || "");
    formData.append("scheduled_for", formRaw.scheduled_for || "");
    formData.append("is_altai", formRaw.is_altai ? "1" : "0");
    formData.append("video_type", formRaw.video_type || "");
    formData.append("text_direction", this.textDirection);
    formData.append("video_type_id", formRaw.video_type_id?.toString() || "");

    if (this.selectedCoverFrameFile) {
      formData.append(
        "rss_img",
        this.selectedCoverFrameFile,
        this.selectedCoverFrameFile.name,
      );

      if (!this.selectedFile) {
        formData.append(
          "image_input",
          this.selectedCoverFrameFile,
          this.selectedCoverFrameFile.name,
        );
      }
    }

    const videoType = formRaw.video_type;

    if (videoType === "video_upload" && formRaw.video_file) {
      formData.append("video_file", formRaw.video_file);
    }

    if (videoType === "video_url" && formRaw.video_url) {
      formData.append("video_url", formRaw.video_url);
    }

    if (this.selectedFile) {
      formData.append("image_input", this.selectedFile);
    }

    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((uf, i) => {
        if (uf.file) {
          // Regular uploaded files
          formData.append(`image_array[${i}]`, uf.file);
        } else if (uf.preview) {
          // AI-generated images (remote URLs)
          formData.append(`image_url_array[${i}]`, uf.preview);
        }
        formData.append(`alt_text_array[${i}]`, uf.alt || "");
      });
    }

    const endpoint = `manual-post/storePost`;

    this.coreService
      .postData(endpoint, formData)
      .pipe(
        take(1),
        catchError((err) => {
          this.coreService.showSnackbar(
            "No posting slots found. Add a slot to start scheduling posts.",
          );
          console.error(err);
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (!res) return;

        this.coreService.showSnackbar("Post created successfully!");
        this.onSubmitClbk.emit(res);

        this.postForm.reset();
        this.feeds = [];
        this.filteredPlatforms = [];
        this.filteredTags = [];
        this.selectedFile = null;
        this.imagePreview = null;
        this.uploadedFiles = [];

        this.router.navigate([
          "/preview-post/" + res.social_tag + "/" + res.post_id,
        ]);
      });
  }

  private loadSidebarPosts(): void {
    if (typeof window === "undefined") {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Post[] | null;
        this.posts = Array.isArray(parsed)
          ? parsed.slice(0, this.maxSidebarCards)
          : [];
      } catch (e) {
        console.error("Failed to parse latest posts from localStorage", e);
        this.posts = [];
      }
    }

    this.coreService.latestPosts$.subscribe(posts => {
      if (posts && posts.length > 0) {
        this.posts = posts.slice(0, this.maxSidebarCards);
      }
    });
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === "undefined") return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
          this.alerts = parsed.slice(0, this.maxSidebarCards);
      } catch (e) {
        console.error("Failed to parse alerts from storage", e);
      }
    }

    this.coreService.latestAlerts$.subscribe(alerts => {
      if (alerts && alerts.length > 0) {
        this.alerts = alerts.slice(0, this.maxSidebarCards);
      }
    });
  }

  private async generateCoverFramesFromVideo(file: File): Promise<void> {
    const videoUrl = URL.createObjectURL(file);

    try {
      const video = document.createElement("video");
      video.src = videoUrl;
      video.muted = true;
      video.playsInline = true;
      video.preload = "metadata";

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () =>
          reject(new Error("Failed to load video metadata"));
      });

      const duration = video.duration;
      if (!isFinite(duration) || duration <= 0) return;

      const times = this.buildFrameTimes(duration, this.COVER_FRAME_COUNT);
      const frames: { time: number; dataUrl: string }[] = [];

      for (const t of times) {
        const dataUrl = await this.captureFrameAt(video, t);
        frames.push({ time: t, dataUrl });
      }

      this.coverFrames = frames;

      if (frames.length) this.selectCoverFrame(frames[0]);
    } finally {
      URL.revokeObjectURL(videoUrl);
    }
  }

  private buildFrameTimes(duration: number, count: number): number[] {
    const safeStart = Math.min(0.5, duration * 0.1);
    const safeEnd = Math.max(duration - 0.2, safeStart);
    if (safeEnd <= safeStart) return [0];

    const step = (safeEnd - safeStart) / Math.max(count - 1, 1);
    const times: number[] = [];

    for (let i = 0; i < count; i++) {
      times.push(Number((safeStart + step * i).toFixed(2)));
    }
    return times;
  }

  private async captureFrameAt(
    video: HTMLVideoElement,
    time: number,
  ): Promise<string> {
    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        video.removeEventListener("seeked", onSeeked);
        resolve();
      };
      video.addEventListener("seeked", onSeeked);
      video.currentTime = time;
      setTimeout(() => reject(new Error("Seek timeout")), 6000);
    });

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  }

  selectCoverFrame(frame: { time: number; dataUrl: string }) {
    this.selectedCoverFrameDataUrl = frame.dataUrl;
    this.selectedCoverFrameFile = this.dataUrlToFile(
      frame.dataUrl,
      `cover_${Math.round(frame.time * 1000)}.jpg`,
    );

    this.postForm.patchValue({ cover_frame: this.selectedCoverFrameFile });
    this.postForm.get("cover_frame")?.markAsDirty();
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const [meta, base64] = dataUrl.split(",");
    const mimeMatch = meta.match(/data:(.*);base64/);
    const mime = mimeMatch?.[1] || "image/jpeg";

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

    return new File([bytes], fileName, { type: mime });
  }

  private initForm(): void {
    this.postForm = this.fb.group({
      site: ["", Validators.required],
      rss_feed_id: [null, Validators.required],
      platform: ["", Validators.required],
      content_site_id: [""],
      post_status: ["", Validators.required],
      scheduled_for: [null],
      post_title: ["", [Validators.required, Validators.maxLength(255)]],
      tag: ["", Validators.required],
      rss_article_url: [
        "",
        [
          Validators.required,
          Validators.pattern(
            "^(https?:\\/\\/)?([\\w-]+\\.)+[\\w-]+(\\/\\S*)?$",
          ),
        ],
      ],
      rss_description: ["", [Validators.required, Validators.maxLength(902)]],
      image_input: [""],
      image_array: [""],
      alt_text_array: [""],
      social_platform_id: [null],
      is_altai: [false],
      video_type_id: [null],
      video_type: [""],
      video_file: [null],
      video_url: [""],
      cover_frame: [null],
    });
  }
}

